import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { Loader } from '@openx/components-core/lib/Loader/Loader';
import { type AudienceData, AudienceOption } from '@openx/types/targeting/audience';
import { noop } from '@openx/utils/noop';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../utils/context/TargetingContext';
import { DimensionChipLine } from '../shared/DimensionChip/DimensionChipLine';
import { FiltersTitle } from '../shared/FiltersTitle/FiltersTitle';
import {
  TargetingDropdownSection,
  type TargetingDropdownSectionProps,
} from '../shared/ReadOnlyTargetingDropdown/TargetingDropdownSection';
import { SelectedItems } from '../shared/SelectedItems/SelectedItems';
import { Switch } from '../shared/Switch/Switch';
import type { TargetingItemsProps } from '../types';
import { EidsItems } from './components/EidsItems';
import { MultiProviderAudienceAlert } from './components/MultiProviderAudienceAlert';
import { useAudienceItems } from './hooks/useAudienceItems';
import { useAudienceTitles } from './hooks/useAudienceTitles';
import { useAudienceTypeOption } from './hooks/useAudienceTypeOption';

const StyledWrapper = styled(Box)`
  margin-left: ${({ theme }) => theme.spacing(3.5)};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

export const ItemsFormPreview = ({
  targetingParams,
  readonly,
  accountUid,
  onAllAnyOperatorChange = noop,
  isFromCurated = false,
  hideButtons = false,
  onOpenDrawerClick = noop,
  onRemove = noop,
  skipAlertRender = true,
  optionsCount,
  disabledReasonMessage,
}: TargetingItemsProps<AudienceData | null> &
  TargetingDropdownSectionProps & {
    accountUid: string;
    onAllAnyOperatorChange?: () => void;
    skipAlertRender?: boolean;
  }) => {
  const { t } = useTranslation();
  const { labels } = useAudienceTypeOption();
  const { titles } = useAudienceTitles();

  const audienceOptions = useMemo(() => {
    return Object.entries(labels);
  }, [labels]);

  const {
    selectedAudienceType,
    selectedTrafficFilterEidsArray,
    selectedAudienceOption,
    getSelectedItems,
    isLoading,
    audienceTypeLabels,
    optionsMap,
    filtersTitle,
    allAnySwitchState,
    DefaultItemComponent,
    isReadOnly,
    isMinimalAudiencePreview,
  } = useAudienceItems({
    targetingParams,
    accountUid,
    readonly,
    isFromCurated,
  });

  const selectedItems = getSelectedItems();

  const { useGetAudienceMultiproviderInfo } = useTargetingContext();
  const { hasMultiProviderPricingData, loadingAudienceCheck } = useGetAudienceMultiproviderInfo({
    selectedAudienceOption,
    values: selectedItems,
  });

  if (isLoading || loadingAudienceCheck) {
    return <Loader />;
  }

  return (
    <div data-test="sub-section">
      {selectedAudienceOption ? (
        <TargetingDropdownSection
          name={titles[selectedAudienceOption] ?? ''}
          hideButtons={hideButtons}
          isEmpty={false}
          onOpenDrawerClick={() => onOpenDrawerClick(selectedAudienceOption)}
          onRemove={onRemove}
          hasOptionsSelected={!!optionsCount && optionsCount > 0}
        >
          <FiltersTitle
            onClear={noop}
            title={titles[selectedAudienceOption] || ''}
            readonly={isReadOnly}
            disabledReasonMessage={disabledReasonMessage}
          />

          {selectedAudienceOption === AudienceOption.DMP_SEGMENTS && (
            <Switch
              switchState={allAnySwitchState}
              onSwitchChange={onAllAnyOperatorChange}
              switchOffLabel={t('ALL')}
              switchOnLabel={t('ANY')}
              groupTitle={t('THE FOLLOWING:')}
              disabled={isReadOnly}
            />
          )}

          <SelectedItems
            filtersTitle={filtersTitle}
            isAddIcon
            selectedItems={selectedItems}
            ItemComponent={DefaultItemComponent}
            readonly={isReadOnly}
            handleChange={noop}
            options={optionsMap}
            dataTest={selectedAudienceOption.replace('_', '-')}
            isMinimalAudiencePreview={isMinimalAudiencePreview}
          />

          {hasMultiProviderPricingData && !skipAlertRender && <MultiProviderAudienceAlert />}

          {!!selectedTrafficFilterEidsArray.length && (
            <StyledWrapper>
              <EidsItems
                selectedTrafficFilterEidsArray={selectedTrafficFilterEidsArray}
                readonly={isReadOnly}
                onChange={noop}
              />
            </StyledWrapper>
          )}
        </TargetingDropdownSection>
      ) : (
        audienceOptions.map(([key, name], index, arr) => {
          if (key === AudienceOption.INTENDED_AUDIENCE && selectedAudienceType) return null;

          return (
            <Fragment key={key}>
              <TargetingDropdownSection
                name={name}
                isEmpty={true}
                onOpenDrawerClick={() => onOpenDrawerClick(key)}
                onRemove={onRemove}
                hasOptionsSelected={!!optionsCount && optionsCount > 0}
              />
              {index < arr.length - 1 && <DimensionChipLine showLabel={false} />}
            </Fragment>
          );
        })
      )}

      {!!selectedAudienceType && (
        <>
          <DimensionChipLine showLabel={Boolean(selectedAudienceOption)} />

          <TargetingDropdownSection
            name={t('Intended Audience')}
            hideButtons={hideButtons}
            isEmpty={false}
            onOpenDrawerClick={() => onOpenDrawerClick(AudienceOption.INTENDED_AUDIENCE)}
            hasOptionsSelected={!!optionsCount && optionsCount > 0}
          >
            <FiltersTitle
              onClear={noop}
              title={labels[AudienceOption.INTENDED_AUDIENCE] || ''}
              readonly={true}
              disabledReasonMessage={disabledReasonMessage}
            />

            <SelectedItems
              filtersTitle={t('Included')}
              isAddIcon
              selectedItems={[selectedAudienceType.val]}
              readonly={true}
              options={audienceTypeLabels}
              handleChange={noop}
              dataTest="selected-audience-type"
            />
          </TargetingDropdownSection>
        </>
      )}
    </div>
  );
};
